import { Breadcrumb, BreadcrumbItem } from '@/components/Breadcrumb';
import { PATH } from '@/constants/path';
import { CMSContainer } from '@/modules/CMSContainer';
import cx from 'classnames';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import Link from 'next/link';

import styles from './Celebrating20YearsPage.module.scss';
import Lockup from './components/Lockup';
import Milestones from './components/Milestones';
import VideoCard from './components/VideoCard';
import { ASSETS, MAIN_VIDEO, STATS, TESTIMONIALS } from './content';

const title = '20 Years in Good Hands | Medrecruit';
const description =
  "Two decades of making medical recruitment work better for doctors and the hospitals and practices that need them. This is the story of what we've built together.";

const BREADCRUMB_ITEMS: BreadcrumbItem[] = [
  { label: 'Medrecruit', href: PATH.HOME },
  { label: 'About us', href: PATH.ABOUT_US },
  { label: '20 years in good hands' },
];

const Celebrating20YearsPage = () => {
  return (
    <CMSContainer>
      <NextSeo title={title} description={description} openGraph={{ title, description }} />
      <main className={styles.page}>
        {/* Hero */}
        <div className={styles.hero}>
          <div className={cx(styles.container, styles.heroGrid)}>
            <div className={styles.heroCopy}>
              <Breadcrumb items={BREADCRUMB_ITEMS} className={styles.breadcrumb} />

              <Lockup size="hero" />
              <p>
                Two decades ago we set out to make medical recruitment work better, for doctors and
                for the hospitals and practices that need them. This is the story of what we&apos;ve
                built together, and the people it&apos;s been for.
              </p>
            </div>

            {/* The photos are cropped with object-fit: cover into narrow portrait frames, so each
                one renders much wider than its frame; `sizes` reflects that rendered width */}
            <div className={styles.collage}>
              <figure>
                <Image
                  src={`${ASSETS}/collage-carer.jpg`}
                  alt="A carer holding a patient's hands in support"
                  width={992}
                  height={1056}
                  sizes="(max-width: 767px) 40vw, 222px"
                  priority
                />
              </figure>
              <figure>
                <Image
                  src={`${ASSETS}/collage-doctor-female.jpg`}
                  alt="Smiling female doctor in navy scrubs"
                  width={1024}
                  height={572}
                  sizes="(max-width: 767px) 100vw, 565px"
                  priority
                />
              </figure>
              <figure>
                <Image
                  src={`${ASSETS}/collage-doctor-male.jpg`}
                  alt="Male doctor in navy scrubs speaking to camera"
                  width={1824}
                  height={1024}
                  sizes="(max-width: 767px) 110vw, 625px"
                  priority
                />
              </figure>
            </div>
          </div>
        </div>

        {/* Main video */}
        <section className={styles.section} aria-labelledby="doctors-first-title">
          <div className={styles.container}>
            <div className={styles.sectionHead}>
              <h2 id="doctors-first-title">
                20 years of putting <span className={styles.highlight}>doctors first</span>
              </h2>
              <p className={styles.sectionSub}>
                The story of two decades in medical recruitment, told by the doctors and employers
                who lived it
              </p>
            </div>
            {/* On desktop the main video poster, not the hero collage, is the largest element in the
                first viewport */}
            <VideoCard video={MAIN_VIDEO} variant="wide" priority />
          </div>
        </section>

        {/* Stats */}
        <section className={cx(styles.section, styles.impact)} aria-labelledby="impact-title">
          <div className={styles.container}>
            <div className={styles.sectionHead}>
              <h2 id="impact-title">
                20 years of <span className={styles.highlight}>impact</span>
              </h2>
              <p className={cx(styles.sectionSub, styles.sectionSubLarge)}>
                The numbers behind the story
              </p>
            </div>
            <ul className={styles.stats}>
              {STATS.map(stat => (
                <li key={stat.label}>
                  <span className={styles.statNum}>{stat.value}</span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Good words */}
        <section
          className={cx(styles.section, styles.goodWords)}
          aria-labelledby="good-words-title"
        >
          <div className={styles.container}>
            <div className={styles.sectionHead}>
              <h2 id="good-words-title">
                20 years of <span className={styles.highlight}>good words</span>
              </h2>
              <p className={styles.sectionSub}>
                The story of two decades in medical recruitment, told by the doctors and employers
                who lived it
              </p>
            </div>
            <ul className={styles.testimonials}>
              {TESTIMONIALS.map(testimonial => (
                <li className={styles.testimonial} key={testimonial.name}>
                  <VideoCard video={testimonial} variant="portrait" />
                  <h3 className={styles.testimonialName}>{testimonial.name}</h3>
                  <p className={styles.testimonialTopic}>{testimonial.topic}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Milestones />

        {/* Thank-you message */}
        <section className={styles.thanks} aria-label="A message from the Medrecruit team">
          <div className={styles.container}>
            <figure>
              <blockquote>
                <p>
                  Thank you to every doctor and client who&apos;s been part of the last 20 years.{' '}
                  <span className={styles.highlight}>We&apos;re just getting started.</span>
                </p>
              </blockquote>
              <figcaption>Medrecruit team</figcaption>
            </figure>
          </div>
        </section>

        {/* Looking ahead CTA */}
        <section className={styles.cta} aria-labelledby="ahead-title">
          <div className={styles.container}>
            <div className={styles.sectionHead}>
              <h2 id="ahead-title">
                Looking <span className={styles.highlight}>ahead</span>
              </h2>
              <h3>Find your next role, on your terms</h3>
              <p className={styles.ctaBody}>
                Search live vacancies on our job board, or register and let opportunities come to
                you.
              </p>
            </div>
            <Link className={styles.btnPrimary} href={PATH.JOBSLISTSEO}>
              Search jobs
            </Link>
            <p className={styles.ctaNote}>
              Not ready yet? <Link href="/lead/locum-guide-2026">Download our locum guide</Link>
            </p>
          </div>
        </section>
      </main>
    </CMSContainer>
  );
};

export default Celebrating20YearsPage;
